import React, { useState, useEffect } from "react";
import axios from "axios";
import BuildingDestinationField from "../../shared/building-destination";
import MessageBox from "../../shared/form-message-box";
import FormResult from "../../shared/form-result";
import { Tooltip } from "react-tooltip";

declare global {
	interface Window {
		gextAnalyseForm: {
			ajaxUrl: string;
			nonce: string;
			cgvUrl: string;
		};
	}
}

// Helper function to get URL parameters
const getUrlParams = (): URLSearchParams => {
	return new URLSearchParams(window.location.search);
};

export default function AnalyseForm() {
	const gextAnalyseForm = window.gextAnalyseForm;

	// Get URL params for prefilling
	const urlParams = getUrlParams();

	const [destinationImmeuble, setDestinationImmeuble] = useState(
		urlParams.get("destination_immeuble") || "",
	);
	const [hasLargeStorageOrOffices, setHasLargeStorageOrOffices] = useState(
		urlParams.get("has_large_storage_or_offices") || "",
	);
	const [typologieChauffage, setTypologieChauffage] = useState("");
	const [surface, setSurface] = useState(urlParams.get("surface") || "");
	const [pdlOrSiren, setPdlOrSiren] = useState(
		urlParams.get("pdl_or_siren") || "",
	);
	const [address, setAddress] = useState(urlParams.get("address") || "");
	const [email, setEmail] = useState(urlParams.get("email") || "");
	const [phone, setPhone] = useState(urlParams.get("phone") || "");
	const [poste, setPoste] = useState(urlParams.get("poste") || "");
	const [messageText, setMessageText] = useState(
		urlParams.get("message") || "",
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);
		const formData = new FormData(e.currentTarget);
		formData.append("action", "gext_analyse_form_submit");
		formData.append("nonce", gextAnalyseForm.nonce);

		try {
			const response = await axios.postForm(
				gextAnalyseForm.ajaxUrl,
				formData,
			);

			console.log(response);

			const result = response.data;

			if (result.success) {
				setIsSuccess(true);
				setMessage(result.data.message);
			} else {
				setIsSuccess(false);
				setMessage(result.data.message || "Une erreur est survenue.");
			}
		} catch (error) {
			if (axios.isAxiosError(error)) {
				setIsSuccess(false);
				console.log(error.response);
				setMessage(
					error.response?.data.data?.message ||
						"Une erreur est survenue.",
				);
			} else {
				setIsSuccess(false);
				setMessage(
					"Une erreur de connexion est survenue. Veuillez réessayer.",
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			{!isSuccess && (
				<>
					<MessageBox
						message={message}
						isSuccess={isSuccess}
						isLoading={isLoading}
					/>
					<form
						className="gext-form__form"
						onSubmit={onSubmit}
						encType="multipart/form-data"
					>
						<div className="gext-form__fields-grid">
							{/* 1. Usage affecté à l'immeuble */}
							<BuildingDestinationField
								destinationImmeuble={destinationImmeuble}
								setDestinationImmeuble={setDestinationImmeuble}
								hasLargeStorageOrOffices={
									hasLargeStorageOrOffices
								}
								setHasLargeStorageOrOffices={
									setHasLargeStorageOrOffices
								}
							/>

							{/* 2. Typologie de Chauffage */}
							<div className="gext-form__field">
								<label htmlFor="gext-typologie-chauffage">
									Typologie de Chauffage{" "}
									<span className="required">*</span>
								</label>
								<select
									id="gext-typologie-chauffage"
									name="typologie_chauffage"
									required
									value={typologieChauffage}
									onChange={(
										e: React.ChangeEvent<HTMLSelectElement>,
									) => setTypologieChauffage(e.target.value)}
								>
									<option value="">
										Veuillez sélectionner une typologie de
										chauffage
									</option>
									<option value="1">Pompe à chaleur</option>
									<option value="2">Chaudière gaz</option>
									<option value="3">Chaudière fioul</option>
								</select>
							</div>

							{/* 3. Surface du site */}
							<div className="gext-form__field">
								<label htmlFor="surface">
									Surface du site en m²{" "}
									<span className="required">*</span>
								</label>
								<input
									type="number"
									id="surface"
									name="surface"
									required
									min="0"
									step="0.01"
									value={surface}
									onChange={(e) => setSurface(e.target.value)}
								/>
							</div>

							{/* 4. PDL ou SIREN */}
							<div className="gext-form__field">
								<label htmlFor="pdl_or_siren">
									SIREN ou Point De Livraison Enedis (PDL){" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="pdl_or_siren"
									name="pdl_or_siren"
									required
									pattern="\d{9}|\d{14}"
									title="Entrez un SIREN (9 chiffres) ou un PDL (14 chiffres)"
									placeholder="Ex: 123456789 ou 12345678901234"
									value={pdlOrSiren}
									onChange={(e) =>
										setPdlOrSiren(e.target.value)
									}
								/>
								<p className="gext-form__field__help">
									Format attendu : SIREN (9 chiffres) ou PDL
									(14 chiffres)
								</p>
							</div>

							{/* 5. Adresse */}
							<div className="gext-form__field">
								<label htmlFor="address">
									Adresse <span className="required">*</span>
								</label>
								<input
									type="text"
									id="address"
									name="address"
									required
									value={address}
									onChange={(e) => setAddress(e.target.value)}
								/>
							</div>

							{/* 6. Nom complet */}
							<div className="gext-form__field">
								<label htmlFor="fullname">
									Nom complet{" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="fullname"
									name="fullname"
									required
								/>
							</div>

							{/* 7. Email */}
							<div className="gext-form__field">
								<label htmlFor="email">
									Email <span className="required">*</span>
								</label>
								<input
									type="email"
									id="email"
									name="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>

							{/* 8. Téléphone */}
							<div className="gext-form__field">
								<label htmlFor="phone">
									Téléphone{" "}
									<span className="required">*</span>
								</label>
								<input
									type="tel"
									id="phone"
									name="phone"
									required
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
								/>
							</div>

							{/* 9. Entreprise */}
							<div className="gext-form__field">
								<label htmlFor="company">
									Entreprise{" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="company"
									name="company"
									required
								/>
							</div>

							{/* 10. Poste dans l'entreprise (optionnel) */}
							<div className="gext-form__field">
								<label htmlFor="poste">
									Poste dans l'entreprise
								</label>
								<input
									type="text"
									id="poste"
									name="poste"
									value={poste}
									onChange={(e) => setPoste(e.target.value)}
								/>
							</div>

							{/* 11. Facture d'énergie (optionnel) */}
							<div className="gext-form__field gext-form__field--full">
								<label htmlFor="energy_invoice">
									Renseignez une facture d'énergie (Elec et/ou
									gaz){" "}
									<span
										className="gext-form__tooltip"
										data-tooltip-id="energy-invoice-tooltip"
										data-tooltip-content="L'envoi d'une facture d'énergie nous permet de vous répondre plus précisément"
									>
										ⓘ
									</span>
								</label>
								<input
									type="file"
									id="energy_invoice"
									name="energy_invoice"
									accept=".pdf,.jpg,.jpeg,.png,.gif"
								/>
							</div>

							{/* 12. Message (optionnel) */}
							<div className="gext-form__field gext-form__field--full">
								<label htmlFor="message">Message</label>
								<textarea
									id="message"
									name="message"
									placeholder="Votre message"
									rows={4}
									value={messageText}
									onChange={(e) =>
										setMessageText(e.target.value)
									}
								/>
							</div>

							{/* 13. Checkbox CGV */}
							<div className="gext-form__submit_box">
								<div className="gext-form__checkboxe_field">
									<input
										type="checkbox"
										id="accepts_cgv"
										name="accepts_cgv"
										value="1"
										required
									/>
									<label htmlFor="accepts_cgv">
										J'autorise à stocker les informations
										renseignées afin de traiter ma demande
										en accord avec les{" "}
										{gextAnalyseForm.cgvUrl ? (
											<a
												href={gextAnalyseForm.cgvUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												Conditions Générales de Vente
											</a>
										) : (
											"Conditions Générales de Vente"
										)}
									</label>
								</div>

								<div className="gext-form__submit">
									<button type="submit" disabled={isLoading}>
										{isLoading
											? "Envoi en cours..."
											: "Envoyer"}
									</button>
								</div>
							</div>
						</div>
					</form>
				</>
			)}
			{isSuccess && (
				<FormResult
					variant="success"
					title="Demande d'analyse reçue avec succès !"
					descriptions={[
						"Votre demande d'analyse énergétique a bien été enregistrée. Nos experts vont étudier les informations que vous nous avez transmises pour vous proposer un accompagnement sur mesure.",
						"Vous recevrez prochainement un email de confirmation avec les détails de votre demande. Un conseiller vous contactera ensuite pour planifier les prochaines étapes de votre accompagnement.",
						<>
							<strong>Ce qui va suivre :</strong> Analyse de vos
							données de consommation, identification des axes
							d'amélioration, et proposition d'un plan d'action
							personnalisé pour atteindre vos objectifs de
							réduction énergétique dans le cadre du Décret
							Tertiaire.
						</>,
					]}
				/>
			)}
			<Tooltip id="energy-invoice-tooltip" />
		</>
	);
}
